import type { ThreeEvent } from "@react-three/fiber";
import { useFrame, useThree } from "@react-three/fiber";
import { useCallback, useMemo, useRef } from "react";
import * as THREE from "three/webgpu";
import { screenToWorldRadius } from "../screenRadius.ts";
import { resolveSurfaceLoopProfileTarget } from "./surfaceLoopTarget.ts";

const SURFACE_EDGE_HIT_RADIUS_PX = 10;
const SURFACE_EDGE_FALLBACK_LOCAL_RADIUS = 0.02;
const INITIAL_EDGE_HIT_SCALE: [number, number, number] = [1e-5, 1e-5, 1e-5];
const EDGE_HIT_UP = new THREE.Vector3(0, 1, 0);

const EDGE_HIT_MATERIAL = new THREE.MeshBasicMaterial({
  transparent: true,
  opacity: 0,
  colorWrite: false,
  depthWrite: false,
  side: THREE.DoubleSide,
});

export function SurfaceLoopEdgeTargetHits({
  vertices,
  radiusPx = SURFACE_EDGE_HIT_RADIUS_PX,
  onSelectFace,
  onSelectEdge,
}: {
  vertices: ReadonlyArray<readonly [number, number, number]>;
  radiusPx?: number;
  onSelectFace?: (e: ThreeEvent<PointerEvent>) => void;
  onSelectEdge: (edgeIndex: number, e: ThreeEvent<PointerEvent>) => void;
}) {
  const groupRef = useRef<THREE.Group>(null);
  const edgeRadiiRef = useRef<number[]>([]);
  const geometry = useMemo(() => new THREE.CylinderGeometry(1, 1, 1, 10), []);
  const selectTarget = useCallback(
    (fallbackEdgeIndex: number, e: ThreeEvent<PointerEvent>) => {
      const localPoint = groupRef.current
        ? localPointOnLoopPlane(e, groupRef.current, vertices)
        : null;
      const resolveRadius = maxRecordedRadius(edgeRadiiRef.current);
      const target = localPoint
        ? resolveSurfaceLoopProfileTarget(vertices, localPoint, resolveRadius)
        : null;
      if (target?.kind === "face" && onSelectFace) {
        onSelectFace(e);
        return;
      }
      onSelectEdge(
        target?.kind === "edge" ? target.edgeIndex : fallbackEdgeIndex,
        e,
      );
    },
    [vertices, onSelectFace, onSelectEdge],
  );
  const setEdgeRadius = useCallback((edgeIndex: number, radius: number) => {
    edgeRadiiRef.current[edgeIndex] = radius;
  }, []);

  if (vertices.length < 2) return null;
  const edgeTargets = vertices.map((a, edgeIndex) => {
    const b = vertices[(edgeIndex + 1) % vertices.length]!;
    return {
      a,
      b,
      edgeIndex,
      key: surfaceEdgeTargetKey(edgeIndex, a, b),
    };
  });

  return (
    <group ref={groupRef}>
      {edgeTargets.map(({ a, b, edgeIndex, key }) => (
        <SurfaceEdgeTargetHit
          key={key}
          geometry={geometry}
          a={a}
          b={b}
          radiusPx={radiusPx}
          onRadiusChange={(radius) => setEdgeRadius(edgeIndex, radius)}
          onPointerDown={(e) => selectTarget(edgeIndex, e)}
        />
      ))}
    </group>
  );
}

function SurfaceEdgeTargetHit({
  geometry,
  a,
  b,
  radiusPx,
  onRadiusChange,
  onPointerDown,
}: {
  geometry: THREE.CylinderGeometry;
  a: readonly [number, number, number];
  b: readonly [number, number, number];
  radiusPx: number;
  onRadiusChange: (radius: number) => void;
  onPointerDown: (e: ThreeEvent<PointerEvent>) => void;
}) {
  const meshRef = useRef<THREE.Mesh>(null);
  const { camera, size: viewportSize } = useThree();

  useFrame(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const dz = b[2] - a[2];
    const length = Math.hypot(dx, dy, dz);
    if (length < 1e-9) {
      mesh.visible = false;
      return;
    }
    const parent = mesh.parent;
    if (parent) parent.updateWorldMatrix(true, false);

    mesh.visible = true;
    _edgeHitMidpoint.set(
      (a[0] + b[0]) * 0.5,
      (a[1] + b[1]) * 0.5,
      (a[2] + b[2]) * 0.5,
    );
    mesh.position.set(
      _edgeHitMidpoint.x,
      _edgeHitMidpoint.y,
      _edgeHitMidpoint.z,
    );
    mesh.quaternion.setFromUnitVectors(
      EDGE_HIT_UP,
      _edgeHitDirection.set(dx / length, dy / length, dz / length),
    );
    const worldMidpoint = _edgeHitWorldMidpoint.copy(_edgeHitMidpoint);
    const parentScale = parent ? maxMatrixAxisScale(parent.matrixWorld) : 1;
    if (parent) worldMidpoint.applyMatrix4(parent.matrixWorld);
    const worldRadius = screenToWorldRadius(
      radiusPx,
      worldMidpoint,
      camera,
      viewportSize.height,
    );
    const localRadius =
      parentScale > 1e-8
        ? worldRadius / parentScale
        : SURFACE_EDGE_FALLBACK_LOCAL_RADIUS;
    const radius = Math.max(1e-5, localRadius);
    mesh.scale.set(radius, length, radius);
    onRadiusChange(radius);
  });

  return (
    <mesh
      ref={meshRef}
      geometry={geometry}
      material={EDGE_HIT_MATERIAL}
      scale={INITIAL_EDGE_HIT_SCALE}
      userData={{ isGizmoHandle: true }}
      onPointerDown={onPointerDown}
    />
  );
}

const _edgeHitMidpoint = new THREE.Vector3();
const _edgeHitWorldMidpoint = new THREE.Vector3();
const _edgeHitDirection = new THREE.Vector3();

export function maxMatrixAxisScale(matrix: THREE.Matrix4): number {
  const e = matrix.elements;
  return Math.max(
    Math.hypot(e[0], e[1], e[2]),
    Math.hypot(e[4], e[5], e[6]),
    Math.hypot(e[8], e[9], e[10]),
  );
}

function maxRecordedRadius(radii: readonly number[]): number {
  let radius = SURFACE_EDGE_FALLBACK_LOCAL_RADIUS;
  for (const value of radii) {
    if (Number.isFinite(value)) radius = Math.max(radius, value);
  }
  return radius;
}

function localPointOnLoopPlane(
  e: ThreeEvent<PointerEvent>,
  group: THREE.Group,
  vertices: ReadonlyArray<readonly [number, number, number]>,
): [number, number, number] | null {
  group.updateWorldMatrix(true, false);
  const worldPlane = loopWorldPlane(vertices, group.matrixWorld);
  const worldPoint = new THREE.Vector3();
  if (!worldPlane || !e.ray.intersectPlane(worldPlane, worldPoint)) {
    worldPoint.copy(e.point);
  }

  const inverse = new THREE.Matrix4().copy(group.matrixWorld).invert();
  worldPoint.applyMatrix4(inverse);
  return [worldPoint.x, worldPoint.y, worldPoint.z];
}

function loopWorldPlane(
  vertices: ReadonlyArray<readonly [number, number, number]>,
  matrixWorld: THREE.Matrix4,
): THREE.Plane | null {
  if (vertices.length < 3) return null;
  const worldVertices = vertices.map((vertex) =>
    new THREE.Vector3(vertex[0], vertex[1], vertex[2]).applyMatrix4(
      matrixWorld,
    ),
  );
  const normal = new THREE.Vector3();
  for (let i = 0; i < worldVertices.length; i += 1) {
    const a = worldVertices[i]!;
    const b = worldVertices[(i + 1) % worldVertices.length]!;
    normal.x += (a.y - b.y) * (a.z + b.z);
    normal.y += (a.z - b.z) * (a.x + b.x);
    normal.z += (a.x - b.x) * (a.y + b.y);
  }
  if (normal.lengthSq() < 1e-18) return null;
  normal.normalize();
  return new THREE.Plane().setFromNormalAndCoplanarPoint(
    normal,
    worldVertices[0]!,
  );
}

export function consumeSurfaceTargetPointer(e: ThreeEvent<PointerEvent>): void {
  e.stopPropagation();
  e.nativeEvent.stopImmediatePropagation();
}

function surfaceEdgeTargetKey(
  edgeIndex: number,
  a: readonly [number, number, number],
  b: readonly [number, number, number],
): string {
  return `${edgeIndex}:${a[0]},${a[1]},${a[2]}:${b[0]},${b[1]},${b[2]}`;
}
